import { EntityType } from "@mapmycustomers/shared/enum";
import { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import { EditorRouteStop } from "../types/RouteStop";

import correlateManualStopsToPreview from "./correlateManualStopsToPreview";

/**
 * Reorder editor stops to match the optimized preview order and renumber
 * `displayOrder` from 1. Stops missing from `routePreview.stopTime` keep
 * their relative order at the tail. Preview-derived fields (`arrivalTime`,
 * `travelDistance`, `travelTime`) are intentionally not written onto stops;
 * they're layered at read time by `getRouteEditorDisplayStops`.
 *
 * Matches preview stops to editor stops by `entity` discriminator + the
 * variant's entity id (`accountId` for company stops, `contactId` for person
 * stops). Manual editor stops are matched positionally via
 * `correlateManualStopsToPreview` using the preview stop's `originalIndex`.
 *
 * Returns the input array reference unchanged when there is no preview to
 * sort by.
 */
const reorderStopsByPreview = (
  stops: EditorRouteStop[],
  routePreview: RoutePreview | undefined
): EditorRouteStop[] => {
  const previewStops = routePreview?.stopTime;
  if (!previewStops) {
    return stops;
  }

  const positionByKey = new Map<string, number>();
  previewStops.forEach((previewStop, index) => {
    if (previewStop.entity === EntityType.COMPANY) {
      positionByKey.set(`${EntityType.COMPANY}:${previewStop.accountId}`, index);
    } else if (previewStop.entity === EntityType.PERSON) {
      positionByKey.set(`${EntityType.PERSON}:${previewStop.contactId}`, index);
    }
  });

  // correlateManualStopsToPreview pairs manual stops positionally against the preview's
  // originalIndex, which indexes the request — and the request excludes checked-off stops.
  // Walking an unfiltered list here would shift every later manual stop's match by one
  // (same positional-pairing constraint as the View selector's identical guard).
  const manualMatches = correlateManualStopsToPreview(
    stops.filter((stop) => !stop.checkedOff),
    previewStops
  );
  const TAIL = Number.POSITIVE_INFINITY;
  const indexed = stops.map((stop, naturalIndex) => {
    let position = TAIL;
    if (stop.entity === EntityType.COMPANY) {
      const key = `${EntityType.COMPANY}:${stop.accountId}`;
      position = positionByKey.has(key) ? positionByKey.get(key)! : TAIL;
    } else if (stop.entity === EntityType.PERSON) {
      const key = `${EntityType.PERSON}:${stop.contactId}`;
      position = positionByKey.has(key) ? positionByKey.get(key)! : TAIL;
    } else if (stop.entity === null && isDefined(stop.id)) {
      position = manualMatches.get(stop.id)?.previewIndex ?? TAIL;
    }
    return { naturalIndex, position, stop };
  });

  indexed.sort((left, right) => {
    if (left.position !== right.position) {
      return left.position - right.position;
    }
    return left.naturalIndex - right.naturalIndex;
  });

  return indexed.map(({ stop }, index) => ({ ...stop, displayOrder: index + 1 }));
};

export default reorderStopsByPreview;
